// Single source of truth for the splash timing, shared by the CSS keyframes in
// styles/pages/index.styled.js, the sheen in components/logo, the header and
// cookie banner's arrival, and the JS timers in pages/index.js.
//
// These are the prototype's own interaction settings, not a reconstruction.
// There are *three* of them, on two different objects:
//
//   the lockup instance     after  100ms · Change to Variant2 · Ease in         · 1000ms
//   LOGO - Shimmer gold 2   after   10ms · Change to Variant2 · Ease in and out · 1800ms
//   Landing 1 → Landing 2   after 2000ms · Smart animate      · Ease in and out · 1200ms
//   Landing 2 → Landing 3   after    1ms · Smart animate      · Ease out        · 1000ms
//
// The first two are separate objects and run independently of the frame changes:
// the instance's variant change is the lockup *arriving*, while the one on the
// `LOGO - Shimmer gold 2` component is its Default → Variant2 pair — the gradient
// moving across the letterforms, i.e. the sheen. Smart animate interpolates every
// layer that differs, so each of the last two arrows is one beat with several
// things moving inside it:
//
//   0.1 → 1.1s   the lockup arrives, ease-in
//   0.01 → 1.81s the sheen passes across it, ease-in-out
//   2.0 → 3.2s   beat A, ease-in-out — the CG gives way to the blurred hero
//   3.2 → 4.2s   beat B, ease-out — the blur clears and the lockup dissolves
//
// The chrome used to ride beat B too. It now comes in on its own beat once the
// overlay has gone — beat C at the foot of this file.
//
// Watch out when checking these against a screen recording: with ease-in-out and
// ease-out the *visible* middle of a transition sits well inside its bounds, so
// measured start/end points read narrower than the real duration.
export const SPLASH_EASE_IN = 'ease-in'
export const SPLASH_EASE_IN_OUT = 'ease-in-out'
export const SPLASH_EASE_OUT = 'ease-out'

// ----- the lockup's own interaction -----
export const SPLASH_LOGO_DELAY = 0.1
export const SPLASH_LOGO_FADE = 1

// ----- the sheen's own interaction -----
// Its own object, its own curve — it starts almost immediately and outlasts the
// lockup's arrival by 0.7s.
export const SPLASH_SHEEN_DELAY = 0.01
export const SPLASH_SHEEN_DURATION = 1.8

// ----- beat A -----
// The splash's own footage steps aside; the lockup is already settled by now.
export const SPLASH_MEDIA_SWAP = 2
export const SPLASH_MEDIA_FADE = 1.4

// ----- beat B -----
// Nothing here resolves on its own: the lockup has no fade-out (it goes with the
// overlay) and the hero's blur clears on the same curve.
//
// B deliberately **starts before A has finished**. Back to back — A ending on
// 3.2 exactly as B began — the two read as two separate events with a dead beat
// between them: A eases *out* into a full stop, B eased *in* from zero, so the
// picture settled, held, and then moved again. Overlapping them by 0.6s and
// putting both on the same ease-in-out turns it into one continuous move, which
// is all "it needs to blend more" was asking for.
export const SPLASH_HOLD = 2.8
export const SPLASH_FADE = 1.6

// How the hero's focus pull gives its scale back — its own window and its own
// curve, both different from the blur it rides with.
//
// The scale is not decoration: blur() samples past the element's edges, so a
// full-bleed layer blurred by 30px shows a soft transparent border, and scaling
// it up crops that out of the clip. The trouble was that it used to travel on
// exactly the blur's window and curve, so the whole 6% unwound *while the
// picture was sharpening into view* — which is the "zoom" people were seeing.
//
// The fix is to spend the travel where it cannot be seen. Under blur, motion
// does not read; detail only comes back over roughly the blur's last third. So
// the scale wants a curve that is nearly finished by then, which a plain
// ease-out is not — it is only ~65% through at that point, leaving a good 15px
// of drift on a sharp picture. This one is ~96% through instead, and the couple
// of pixels left are slower than the eye picks up.
export const SPLASH_FOCUS_SETTLE = 1.8
export const SPLASH_FOCUS_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// When the splash is fully done, in ms — what pages/index.js sets its timer to.
export const SPLASH_END_MS = (SPLASH_HOLD + SPLASH_FADE) * 1000

// ----- beat C: the chrome arrives -----
// The header and the cookie banner come in together, from opposite edges, once
// the overlay has gone. Both hang off `showSplash` flipping false at
// SPLASH_END_MS and share these three numbers, so the two cannot drift apart —
// they used to be a second and a bit apart, the header riding the dissolve and
// the banner waiting for the unmount, and read as two separate events.
//
// The delay is on the way in only; leaving, they go straight out.
export const CHROME_IN_DELAY = 0
export const CHROME_IN_FADE = 1

// How far each travels, in design px. A percentage is no use to the header: its
// children are all absolutely positioned, so its own box is zero-height and
// translateY(-100%) moved it precisely nowhere — which is why the header read as
// a plain fade however the transform was written.
export const CHROME_IN_SHIFT = 40

// The copy panel needs nothing here. It sits behind the hero again, so the deck
// arrives at it and the arrival is what starts its stagger — see the reveal
// effect in pages/index.js. (While the hero was out it was panel 0, with nothing
// to arrive on it, and took its start from a SPLASH_COPY_RELEASE constant here.)
